import React, { useState, useEffect } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './EditTeacherDialog.css';

const KNOWN_DESIGNATIONS = ['চেয়ারম্যান','অধ্যাপক','সহযোগী অধ্যাপক','সহকারী অধ্যাপক','প্রভাষক','সেকশন অফিসার','ডেপুটি রেজিস্টার','সহকারী রেজিস্টার','উপ পরিচালক','সহকারী পরিচালক','ল্যাব সহকারী','MLSS'];

const EditTeacherDialog = ({ teacher, faculty, department, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    FacultyName: '',
    departmentId: '',
    dept_ref: '',
    designation: '',
    phoneNumber: '',
    dp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isCustomDesignation, setIsCustomDesignation] = useState(false);
  const [customDesignationText, setCustomDesignationText] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!selectedFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    if (teacher && faculty && department && isOpen) {
      const isCustom = !!teacher.designation && !KNOWN_DESIGNATIONS.includes(teacher.designation);
      setIsCustomDesignation(isCustom);
      setCustomDesignationText(isCustom ? teacher.designation : '');
      setFormData({
        name: teacher.name || '',
        nameEn: teacher.nameEn || '',
        email: teacher.email || '',
        FacultyName: faculty.name,
        departmentId: department.id || 1,
        dept_ref: department._id,
        designation: isCustom ? 'custom' : (teacher.designation || ''),
        phoneNumber: teacher.phoneNumber || '',
        dp: teacher.dp || ''
      });
      setError('');
      setSelectedFile(null);
      setUploading(false);
    }
  }, [teacher, faculty, department, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'designation') {
      if (value === 'custom') {
        setIsCustomDesignation(true);
        setFormData({ ...formData, designation: 'custom' });
      } else {
        setIsCustomDesignation(false);
        setCustomDesignationText('');
        setFormData({ ...formData, designation: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      // Open cropper modal instead of directly showing preview
      setShowCropper(true);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        dp: e.target.result // Set preview URL temporarily
      }));
    };
    reader.readAsDataURL(croppedBlob);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const result = await uploadMedia(selectedFile);
      if (result.success) {
        return result.url;
      } else {
        setError('Upload failed: ' + (result.error || 'Unknown error'));
        return null;
      }
    } catch (error) {
      setError('Upload failed: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDP = () => {
    setFormData(prev => ({
      ...prev,
      dp: ''
    }));
    setSelectedFile(null);
    const fileInput = document.getElementById('dp-upload-edit');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    const actualDesignation = isCustomDesignation ? customDesignationText.trim() : formData.designation;
    if (!formData.name.trim() || !formData.email.trim() || !actualDesignation || !formData.phoneNumber.trim()) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      // Upload DP if selected
      let dpUrl = formData.dp; // Keep existing URL if no new file
      if (selectedFile) {
        const uploadedUrl = await uploadSelectedFile();
        if (uploadedUrl === null) {
          setLoading(false);
          return; // Upload failed, error already set
        }
        dpUrl = uploadedUrl;
      }

      // Map fields to match backend expectations
      const submitData = {
        name: formData.name,
        nameEn: formData.nameEn,
        email: formData.email,
        facultyName: formData.FacultyName, // Convert to lowercase 'f' to match backend
        departmentId: formData.departmentId,
        dept_ref: formData.dept_ref,
        designation: isCustomDesignation ? customDesignationText.trim() : formData.designation,
        phoneNumber: formData.phoneNumber,
        dp: dpUrl || null,
        priority: teacher.priority || 1 // Preserve original priority or default to 1
      };

      const response = await api.put(`/teachers/${teacher._id}`, submitData);
      
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update teacher');
      }
    } catch (err) {
      console.error('Error updating teacher:', err);
      if (err.response?.status === 400) {
        setError(`Invalid data. Please check your input. Server response: ${err.response.data.message || 'Unknown error'}`);
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Teacher not found.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !teacher || !faculty || !department) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Edit Teacher</h2>
          <div className="context-info">
            <p className="faculty-info">Faculty: {faculty.name}</p>
            <p className="department-info">Department: {department.name}</p>
          </div>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Teacher Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter teacher's full name"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="nameEn">English Name</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              placeholder="Enter teacher's English name"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter teacher's email"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <select
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">Select designation</option>
              <option value="চেয়ারম্যান">চেয়ারম্যান</option>
              <option value="অধ্যাপক">অধ্যাপক</option>
              <option value="সহযোগী অধ্যাপক">সহযোগী অধ্যাপক</option>
              <option value="সহকারী অধ্যাপক">সহকারী অধ্যাপক</option>
              <option value="প্রভাষক">প্রভাষক</option>
              <option value="সেকশন অফিসার">সেকশন অফিসার</option>
              <option value="ডেপুটি রেজিস্টার">ডেপুটি রেজিস্টার</option>
              <option value="সহকারী রেজিস্টার">সহকারী রেজিস্টার</option>
              <option value="উপ পরিচালক">উপ পরিচালক</option>
              <option value="সহকারী পরিচালক">সহকারী পরিচালক</option>
              <option value="ল্যাব সহকারী">ল্যাব সহকারী</option>
              <option value="MLSS">MLSS</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          
          {isCustomDesignation && (
            <div className="form-group">
              <label htmlFor="customDesignation">Custom Designation *</label>
              <input
                type="text"
                id="customDesignation"
                name="customDesignation"
                placeholder="Enter custom designation"
                value={customDesignationText}
                disabled={loading}
                onChange={(e) => setCustomDesignationText(e.target.value)}
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              placeholder="Enter phone number (e.g., +8801234567890)"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="dp-upload-edit">Profile Picture</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || formData.dp ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('dp-upload-edit').click()}
              >
                {formData.dp ? (
                  <img src={formData.dp} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Profile" 
                    className="avatar-image preview" 
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="dp-upload-edit"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {(selectedFile || formData.dp) && (
                <div className="avatar-actions">
                  {selectedFile && !formData.dp && (
                    <button 
                      type="button"
                      onClick={uploadSelectedFile}
                      disabled={loading || uploading}
                      className="upload-button"
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                  {formData.dp && (
                    <button 
                      type="button" 
                      onClick={handleRemoveDP}
                      className="remove-button"
                      disabled={loading || uploading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="dialog-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EditTeacherDialog;
